import { Injectable, inject, signal, OnDestroy } from '@angular/core';
import { Subject, forkJoin, Observable, of, switchMap } from 'rxjs';
import { catchError, finalize, map, takeUntil } from 'rxjs/operators';
import { TicketService } from '../data/ticket.service';
import { LocationService } from '../data/location.service';
import { TicketClosingField } from '../../models/Ticket/TicketClosingField';
import { TaskClosingField } from '../../models/Ticket/TaskClosingField';
import { EvidenceSection, EvidenceImage } from '../../models/applicant-record/evidence.model';
import { TicketRow } from '../../models/Ticket/TicketRow';
import { TicketItem } from '../../models/Ticket/TicketItem';
import { CLOSING_FIELD_CODE } from '../../data';
import { Location, PagedTicketResponse } from '../../models';
import { SnackbarService } from '../base/snackbar.service';
import { TranslationService } from '../base/translation.service';
import { environment } from '../../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class TicketDetailStore implements OnDestroy {

  // region Injected Services 
  private readonly locationService = inject(LocationService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly ticketService = inject(TicketService);
  private readonly translationService = inject(TranslationService);

  // region Lifecycle
  private readonly destroy$ = new Subject<void>();

  // region Signals 
  public approvalField = signal<{
    fieldId: number;
    fieldTypeId: number;
    regex: string;
    value: string;
    approveValue: string;
    rejectValue: string;
  } | null>(null);
  public closingFields = signal<TicketClosingField[]>([]);
  public isLoading = signal(false);
  public locationDetail = signal<Location | null>(null);
  public photoEvidence = signal<Map<string, EvidenceSection>>(new Map());
  public photoEvidenceLoading = signal(true);
  public ticket = signal<TicketRow | null>(null);
  public ticketInformationId = signal<number | null>(null);
  public ticketValidationId = signal<number | null>(null);
  public validationTicketState = signal<string>('');

  // region Computed
  public photoEvidenceSections = (): EvidenceSection[] => Array.from(this.photoEvidence().values());

  // region Lifecycle Methods
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // region Public API
  public load(odt: string, locationKey: string): void {
    if (!locationKey) {
      this.clean();
      return;
    }

    this.clean();
    this.isLoading.set(true);

    forkJoin({
      tickets: this.searchTicketsByOdt(odt),
      location: this.getLocationDetail(environment.locationCustomerId, locationKey),
    }).pipe(
      switchMap(({ tickets, location }) => this.processTickets(tickets, odt, location)),
      catchError(() => {
        this.isLoading.set(false);
        return of(null);
      }),
      finalize(() => this.isLoading.set(false)),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.loadClosingFields();
      this.loadTaskClosingFields();
    });
  }

  public loadPhotoEvidence(fields: TicketClosingField[]): void {
    const fileFields = fields.filter(f => f.ComponentType === 'File');
    if (!fileFields.length) {
      this.photoEvidence.set(new Map());
      return;
    }

    this.photoEvidenceLoading.set(true);
    this.photoEvidence.set(new Map());

    const groupedByCategory = new Map<string, EvidenceSection>();
    const attachmentRequests: { field: TicketClosingField; ticketId: number; attachmentId: number }[] = [];

    fileFields.forEach(field => {
      const urlMatch = field.Value?.match(/TicketId=(\d+)&AttachmentId=(\d+)/);
      if (!urlMatch) return;
      attachmentRequests.push({
        field,
        ticketId: parseInt(urlMatch[1], 10),
        attachmentId: parseInt(urlMatch[2], 10),
      });
    });

    if (!attachmentRequests.length) {
      this.photoEvidenceLoading.set(false);
      return;
    }

    forkJoin(
      attachmentRequests.map(req =>
        this.ticketService.getAttachmentUrl(req.ticketId, req.attachmentId).pipe(
          catchError(() => of(null))
        )
      )
    ).pipe(takeUntil(this.destroy$)).subscribe(responses => {
      attachmentRequests.forEach((req, index) => {
        const response = responses[index];
        if (!response?.Data) return;

        const category = req.field.ClosingFieldCategory || 'Evidencia';
        const image: EvidenceImage = {
          id: `${req.attachmentId}`,
          fullUrl: response.Data.Url,
          fileName: response.Data.FileName,
          fileDescription: req.field.ClosingFieldDescription,
          contentType: response.Data.ContentType,
          md5: response.Data.MD5,
          type: response.Data.ContentType.split('/')[1]?.toUpperCase() || 'FILE',
          size: '',
        };

        if (groupedByCategory.has(category)) {
          groupedByCategory.get(category)!.images.push(image);
        } else {
          groupedByCategory.set(category, { id: category, name: category, images: [image] });
        }
      });

      this.photoEvidence.set(new Map(groupedByCategory));
      this.photoEvidenceLoading.set(false);
    });
  }

  // region Coordinate Getters
  public getLatitudLongitudUser(): [number, number] | null {
    const field = this.closingFields().find(f => f.Code === CLOSING_FIELD_CODE.LATITUD_LONGITUD_USER);
    if (!field?.Value) return null;
    const coords = field.Value.split(',').map(Number);
    return coords.length === 2 ? [coords[0], coords[1]] : null;
  }

  public getLatitudLongitudFormulario(): [number, number] | null {
    const field = this.closingFields().find(f => f.Code === CLOSING_FIELD_CODE.LATITUD_LONGITUD_FORMULARIO);
    if (!field?.Value) return null;
    const coords = field.Value.split(',').map(Number);
    return coords.length === 2 ? [coords[0], coords[1]] : null;
  }

  public getLatitudLongitudEvidencias(): [number, number] | null {
    const field = this.closingFields().find(f => f.Code === CLOSING_FIELD_CODE.LATITUD_LONGITUD_EVIDENCIAS);
    if (!field?.Value) return null;
    const coords = field.Value.split(',').map(Number);
    return coords.length === 2 ? [coords[0], coords[1]] : null;
  }

  // region Private - Loading
  private loadClosingFields(): void {
    const ticketId = this.ticketValidationId() || this.ticketInformationId();
    if (!ticketId) return;

    this.ticketService.getClosingFieldsByTicketId(ticketId).pipe(
      takeUntil(this.destroy$)
    ).subscribe(response => {
      if (response?.Data) {
        this.closingFields.set(response.Data);
      }
    });
  }

  private loadTaskClosingFields(): void {
    const validationId = this.ticketValidationId();
    if (!validationId) return;

    this.ticketService.getTicketDetail(validationId).pipe(
      takeUntil(this.destroy$)
    ).subscribe(response => {
      if (!response?.Data) return;
      this.findApprovalFieldFromTaskClosingFields(response.Data?.TaskClosingFields || []);
    });
  }

  // region Private - Processing
  private processTickets(
    tickets: { openTickets?: PagedTicketResponse; closedTickets?: PagedTicketResponse } | null,
    odt: string,
    location: Location | null
  ): Observable<number | null> {
    if (!tickets || !location) return of(null);

    const allTickets: TicketItem[] = [
      ...(tickets.openTickets?.Items || []),
      ...(tickets.closedTickets?.Items || []),
    ];

    if (!allTickets.length) {
      this.isLoading.set(false);
      return of(null);
    }

    this.identifyTicketIds(allTickets);
    this.setTicketData(allTickets[0], odt, location);

    const closingFieldsTicketId = this.ticketValidationId() || this.ticketInformationId();
    if (!closingFieldsTicketId) {
      this.isLoading.set(false);
      return of(null);
    }
    return of(closingFieldsTicketId);
  }

  private identifyTicketIds(tickets: TicketItem[]): void {
    const validationTicket = tickets.find(t => t.Task.includes('Validación De Información'));
    const informationTicket = tickets.find(t => t.Task.includes('Captura De Información'));

    this.ticketValidationId.set(validationTicket?.Id || null);
    this.ticketInformationId.set(informationTicket?.Id || null);
    this.validationTicketState.set(validationTicket?.TicketState || '');
  }

  private setTicketData(mainTicket: TicketItem, odt: string, location: Location | null): void {
    this.locationDetail.set(location || null);

    this.ticket.set({
      id: mainTicket.Id,
      folio: mainTicket.ODT || odt,
      workflow: mainTicket.WorkFlow || '',
      solicitante: location?.Description || '',
      curp: location?.Curp || '',
      telefono: location?.PhoneNumber || '',
      estatus: mainTicket.TicketState || '',
      direccion: location?.Address || '',
      analista: mainTicket.Engineer || '',
    });
  }

  // region Private - API Calls
  private searchTicketsByOdt(odt: string): Observable<{ openTickets: PagedTicketResponse; closedTickets: PagedTicketResponse } | null> {
    return forkJoin({
      openTickets: this.ticketService.searchOpenTicketsByOdt({ page: 1, pageSize: 10, searchFieldValue: odt }),
      closedTickets: this.ticketService.searchClosedTicketsByOdt({ page: 1, pageSize: 10, searchFieldValue: odt }),
    }).pipe(
      map(({ openTickets, closedTickets }) => {
        if (!openTickets || !closedTickets) {
          this.snackbarService.info(this.translationService.instant('tickets.detail.noTicketsFound'), 6000);
          return null;
        }
        return {
          openTickets: openTickets?.Data,
          closedTickets: closedTickets?.Data,
        };
      }),
      catchError(() => of(null))
    );
  }

  private getLocationDetail(customerId: number, locationKey: string): Observable<Location | null> {
    return this.locationService.getDetailByKey(customerId, locationKey).pipe(
      map(response => {
        if (response?.Data) {
          return response.Data;
        } else {
          this.snackbarService.info(this.translationService.instant('tickets.detail.noLocationFound'), 6000);
          return null;
        }
      })
    );
  }

  // region Private - Helpers
  private clean(): void {
    this.ticket.set(null);
    this.locationDetail.set(null);
    this.closingFields.set([]);
    this.photoEvidence.set(new Map());
    this.approvalField.set(null);
    this.ticketInformationId.set(null);
    this.ticketValidationId.set(null);
    this.validationTicketState.set('');
    this.isLoading.set(true);
    this.photoEvidenceLoading.set(true);
  }

  private findApprovalFieldFromTaskClosingFields(taskClosingFields: TaskClosingField[]): void {
    const taskFields = taskClosingFields;
    if (!taskFields) return;

    const approvalField = taskFields.find(f => f.Code === CLOSING_FIELD_CODE.TICKET_STATUS);
    if (!approvalField) return;

    const regex = approvalField.ValidationRegex || '(Aprobado|Rechazado)';
    const match = regex.match(/^\(([^|]+)\|([^|]+)\|([^)]+)\)$/);
    const approveValue = match?.[1] || 'Aprobado';
    const rejectValue = match?.[2] || 'Rechazado';

    this.approvalField.set({
      fieldId: approvalField.ClosingFieldId,
      fieldTypeId: approvalField.ClosingFieldTypeId,
      regex,
      value: '',
      approveValue,
      rejectValue,
    });
  }
}
